import React, { useState, useEffect } from 'react';
import { IoClose, IoInformationCircle } from 'react-icons/io5';
import { toast, ToastContainer } from 'react-toastify';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const AmenityBooking = ({
  formData,
  setFormData,
  selectedService,
  onClose,
  confirmServiceBooking,
}) => {
  const [subcategories, setSubcategories] = useState([]);
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [showTaxTooltip, setShowTaxTooltip] = useState(false);

  // Tax calculation function - UPDATED
  const calculateTaxesAndCharges = (item, baseAmount, urgentAmount, urgencyLevel) => {
    if (!item || !item.value || !item.value_type) {
      return {
        taxAmount: 0,
        taxLabel: 'Taxes & Charges',
        subtotal: urgencyLevel === 'Urgent' ? urgentAmount : baseAmount,
        finalAmount: urgencyLevel === 'Urgent' ? urgentAmount : baseAmount,
        cgstAmount: 0,
        sgstAmount: 0,
      };
    }

    const { value, value_type } = item;

    // Calculate subtotal (include urgent surcharge if applicable)
    const subtotal = urgencyLevel === 'Urgent' ? urgentAmount : baseAmount;

    let taxAmount = 0;
    let taxLabel = 'Taxes & Charges';

    if (value_type === 'P') {
      // Percentage
      taxAmount = (subtotal * value) / 100;
      taxLabel = `Taxes & Charges `;
    } else if (value_type === 'F') {
      // Fixed amount
      taxAmount = value;
      taxLabel = 'Taxes & Charges';
    }

    // Calculate CGST and SGST (50% each of total tax)
    const cgstAmount = taxAmount / 2;
    const sgstAmount = taxAmount / 2;

    return {
      taxAmount,
      taxLabel,
      subtotal: subtotal,
      finalAmount: subtotal + taxAmount,
      cgstAmount,
      sgstAmount,
    };
  };

  // Fetch all items once when component mounts
  useEffect(() => {
    const fetchAllItems = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityitems/`);
        const data = await response.json();
        setAllItems(data);
      } catch (error) {
        console.error('Error fetching all items:', error);
      }
    };
    fetchAllItems();
  }, []);

  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityservices/`);
        const data = await response.json();

        // Filter services under the selected category
        const filteredSubcategories = data.filter((sub) => sub.categoryid == selectedService?.id);

        setSubcategories(filteredSubcategories);

        // Reset serviceid when category changes
        setFormData((prev) => ({ ...prev, serviceid: '' }));
      } catch (error) {
        console.error('Error fetching subcategories:', error);
      }
    };

    if (selectedService) {
      fetchSubcategories();
    }
  }, [selectedService, setFormData]);

  useEffect(() => {
    if (formData.serviceid) {
      // Filter items based on service ID
      const filteredItems = allItems.filter((item) => item.serviceid == formData.serviceid);
      setItems(filteredItems);
    } else if (selectedService?.id) {
      // If no service selected but category is selected, show items for that category
      const filteredItems = allItems.filter((item) => item.categoryid == selectedService.id);
      setItems(filteredItems);
    }
  }, [formData.serviceid, allItems, selectedService]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateTimeChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      // Initialize with current date/time if not set
      const currentDate = prev.scheduledDate || new Date().toISOString().split('T')[0];
      const currentTime = prev.scheduledTime || new Date().toTimeString().slice(0, 5);

      let newDate = currentDate;
      let newTime = currentTime;

      if (name === 'scheduledDate') {
        newDate = value;
      } else if (name === 'scheduledTime') {
        newTime = value;
      }

      // Create the combined datetime string for API
      const combinedDateTime = newDate && newTime ? `${newDate}T${newTime}:00` : null;

      return {
        ...prev,
        scheduledDate: newDate,
        scheduledTime: newTime,
        scheduledtime: combinedDateTime, // For API
      };
    });
  };

  const handlePreview = async () => {
    // Only require quantity if an item is selected
    if (formData.itemid && !formData.quantity) {
      alert('Please enter quantity for the selected item');
      return;
    }

    // Validate date and time
    if (!formData.scheduledDate || !formData.scheduledTime) {
      alert('Please select both date and time');
      return;
    }

    try {
      let previewData;
      let selectedItem = null;

      if (formData.itemid) {
        // Get the selected item to ensure we're previewing the correct one
        selectedItem = items.find((item) => item.id == formData.itemid);
        if (!selectedItem) {
          throw new Error('Selected item not found');
        }

        const response = await fetch(
          `${BQ_BASE_URL}/bq/api/amenityrequests/preview/${formData.itemid}/${
            formData.quantity || 1
          }`
        );
        previewData = await response.json();

        // Verify the preview data matches the selected item
        if (previewData.item_name !== selectedItem.name) {
          console.warn("Preview data doesn't match selected item. Using local item data.");
          previewData.base_amount = selectedItem.baseprice * (formData.quantity || 1);
          previewData.urgent_amount = selectedItem.urgentprice * (formData.quantity || 1);
          previewData.item_name = selectedItem.name;
        }

        // Calculate taxes and charges
        const taxCalculation = calculateTaxesAndCharges(
          selectedItem,
          previewData.base_amount,
          previewData.urgent_amount,
          formData.urgencylevel || 'Normal'
        );

        previewData = {
          ...previewData,
          ...taxCalculation,
          selectedItem: selectedItem,
        };
      } else {
        // For default items (no specific item selected)
        previewData = {
          item_name: 'Default Service',
          base_amount: 0,
          urgent_amount: 0,
          quantity: 0,
          taxAmount: 0,
          taxLabel: 'Taxes & Charges',
          subtotal: 0,
          finalAmount: 0,
          cgstAmount: 0,
          sgstAmount: 0,
        };
      }

      setPreviewData(previewData);
      setShowPreview(true);
    } catch (error) {
      console.error('Error fetching preview:', error);

      // Fallback to local calculation if API fails and item is selected
      if (formData.itemid) {
        const selectedItem = items.find((item) => item.id == formData.itemid);
        if (selectedItem) {
          const baseAmount = selectedItem.baseprice * (formData.quantity || 1);
          const urgentAmount = selectedItem.urgentprice * (formData.quantity || 1);

          const taxCalculation = calculateTaxesAndCharges(
            selectedItem,
            baseAmount,
            urgentAmount,
            formData.urgencylevel || 'Normal'
          );

          setPreviewData({
            item_name: selectedItem.name,
            base_amount: baseAmount,
            urgent_amount: urgentAmount,
            quantity: formData.quantity || 1,
            ...taxCalculation,
            selectedItem: selectedItem,
          });
          setShowPreview(true);
        } else {
          alert('Failed to fetch preview data');
        }
      } else {
        setPreviewData({
          item_name: 'Default Service',
          base_amount: 0,
          urgent_amount: 0,
          quantity: 0,
          taxAmount: 0,
          taxLabel: 'Taxes & Charges',
          subtotal: 0,
          finalAmount: 0,
          cgstAmount: 0,
          sgstAmount: 0,
        });
        setShowPreview(true);
      }
    }
  };

  const onBook = async (formData) => {
    const apiUrl = `${BQ_BASE_URL}/bq/api/amenityrequests/`;

    // Validate date and time
    if (!formData.scheduledDate || !formData.scheduledTime) {
      toast.error('Please select both date and time');
      return;
    }

    // Retrieve booking details from localStorage
    const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails')) || {};

    // Extract booking ID from nested structure
    const bookingId =
      bookingDetails?.primaryBooking?.booking_id ||
      bookingDetails?.bookings?.[0]?.booking_id ||
      bookingDetails?.bookingid;

    if (!bookingId || typeof bookingId !== 'string') {
      toast.error('Invalid booking ID. Please try again.');
      return;
    }

    // Calculate the amount only if an item is selected
    let amount = 0;
    let selectedItem = null;

    if (formData.itemid) {
      selectedItem = items.find((item) => item.id == formData.itemid);
      if (!selectedItem) {
        toast.error('Selected item not found. Please try again.');
        return;
      }

      const baseAmount = selectedItem.baseprice * (formData.quantity || 1);
      const urgentAmount = selectedItem.urgentprice * (formData.quantity || 1);

      const subtotal = formData.urgencylevel === 'Urgent' ? urgentAmount : baseAmount;

      let taxAmount = 0;
      if (selectedItem.value && selectedItem.value_type) {
        if (selectedItem.value_type === 'P') {
          taxAmount = (subtotal * selectedItem.value) / 100;
        } else if (selectedItem.value_type === 'F') {
          taxAmount = selectedItem.value;
        }
      }

      amount = subtotal + taxAmount;
    }

    // Prepare the request payload
    const requestBody = {
      bookingid: bookingId,
      categoryid: selectedService?.id,
      serviceid: formData.serviceid,
      itemid: formData.itemid || null,
      quantity: formData.quantity || null,
      amount: amount,
      urgencylevel: formData.urgencylevel || 'Normal',
      scheduledtime: formData.scheduledtime, // Use the combined datetime
      specialinstructions: formData.specialinstructions || '',
      status: 'Pending',
    };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data);
      toast.success('Amenity service booked successfully!');

      await confirmServiceBooking();
      onClose();

      return data;
    } catch (error) {
      console.error('Error making booking:', error);
      toast.error(`Booking failed: ${error.message}`);
      throw error;
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewData(null);
    setShowTaxTooltip(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDisplayDateTime = () => {
    if (formData.scheduledDate && formData.scheduledTime) {
      const date = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }
    return 'Not scheduled';
  };

  return (
    <>
      {/* Main Booking Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
          <div className="p-6 relative">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold font-poppins text-gray-800">
                  Book {selectedService?.name}
                </h2>
                <p className="text-gray-500 font-poppins text-sm mt-1">
                  Fill in the details to schedule your service
                </p>
              </div>
              <button
                className="text-red-500 hover:text-red-700 transition-colors duration-200"
                onClick={onClose}
              >
                <IoClose size={32} />
              </button>
            </div>

            <input type="hidden" name="categoryid" value={selectedService?.id} />
            <input type="hidden" name="status" value="Pending" />

            <div className="grid grid-cols-2 gap-6">
              {/* Service and Item in one row */}
              <div className="col-span-1">
                <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                  Select a Service
                </label>
                <select
                  name="serviceid"
                  value={formData.serviceid || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border font-poppins border-gray-200 rounded-lg duration-200"
                >
                  <option value="">Select a service</option>
                  {subcategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Items Dropdown - now optional */}
              <div className="col-span-1">
                <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                  Select an Item
                </label>
                <select
                  name="itemid"
                  value={formData.itemid || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 font-poppins border border-gray-200 rounded-lg duration-200"
                >
                  <option value="">Select an item</option>
                  {items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Urgency Level and Quantity in one row */}
              <div className="col-span-1">
                <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                  Urgency Level
                </label>
                <select
                  name="urgencylevel"
                  value={formData.urgencylevel || 'Normal'}
                  onChange={handleInputChange}
                  className="w-full px-4 font-poppins py-2.5 border border-gray-200 rounded-lg duration-200"
                >
                  <option value="Normal">Normal</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>

              <div className="col-span-1">
                <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                  Quantity {formData.itemid && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  name="quantity"
                  placeholder="Enter quantity"
                  min="1"
                  value={formData.quantity || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
                  required={!!formData.itemid}
                  disabled={!formData.itemid}
                />
              </div>

              <div className="col-span-1">
                <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                  Schedule Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="scheduledDate"
                  value={formData.scheduledDate || ''}
                  onChange={handleDateTimeChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full font-poppins px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
                  required
                />
              </div>

              {/* Schedule Time */}
              <div className="col-span-1">
                <label className="block text-gray-700 text-md font-poppins font-medium mb-1">
                  Schedule Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="scheduledTime"
                  value={formData.scheduledTime || ''}
                  onChange={handleDateTimeChange}
                  className="w-full px-4 py-2.5 border font-poppins border-gray-200 rounded-lg duration-200"
                  required
                />
              </div>

              {/* Special Instructions - full width */}
              <div className="col-span-2">
                <label className="block text-gray-700 font-poppins text-md font-medium mb-1">
                  Special Instructions
                </label>
                <textarea
                  name="specialinstructions"
                  value={formData.specialinstructions || ''}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
                  placeholder="Any special requests or instructions"
                ></textarea>
              </div>
            </div>

            {/* Buttons */}
            <div className="mt-8 flex justify-end space-x-4">
              <button
                onClick={handlePreview}
                className="px-10 py-2.5 font-poppins bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Preview
              </button>
              <button
                onClick={async () => {
                  setIsBooking(true);
                  try {
                    await onBook(formData);
                  } catch (error) {
                    // Error is already handled by the toast in onBook
                  } finally {
                    setIsBooking(false);
                  }
                }}
                disabled={isBooking}
                className={`px-6 py-2.5 font-poppins text-white font-medium rounded-lg transition-colors duration-200 bg-black hover:text-gray-300 ${
                  isBooking ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isBooking ? (
                  <span className="flex font-poppins items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  'Book Now'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-scaleIn">
            <div className="relative">
              {/* Header with gradient background */}
              <div className="bg-gradient-to-r from-black to-gray-700 p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold text-white">Order Summary</h2>
                    <p className="text-blue-100 text-sm mt-1">
                      Review your order before confirmation
                    </p>
                  </div>
                  <button
                    className="text-white hover:text-blue-200 transition-colors duration-200 p-1 rounded-full hover:bg-white/10"
                    onClick={closePreview}
                  >
                    <IoClose size={24} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-5">
                {/* Order Items Card */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <svg
                        className="w-6 h-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">
                        {formData.itemid ? previewData?.item_name : 'Default Service'}
                      </h3>
                      {formData.itemid && (
                        <p className="text-sm text-gray-500">Qty: {previewData?.quantity}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Urgency:</span>
                      <span
                        className={`font-medium ${
                          formData.urgencylevel === 'Urgent' ? 'text-red-500' : 'text-gray-700'
                        }`}
                      >
                        {formData.urgencylevel || 'Normal'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Scheduled for:</span>
                      <span className="font-medium text-gray-700">{formatDisplayDateTime()}</span>
                    </div>
                  </div>
                </div>

                {/* Pricing Breakdown - only show if item is selected */}
                {formData.itemid && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <h4 className="font-semibold text-black mb-4 text-lg">Price Details</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-black">Subtotal:</span>
                        <span className="font-medium">
                          ₹{formatCurrency(previewData?.subtotal || 0)}
                          {formData.urgencylevel === 'Urgent' && (
                            <span className="text-xs text-gray-500 ml-1"></span>
                          )}
                        </span>
                      </div>

                      {/* Taxes and Charges with Info Icon */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-1">
                          <span className="text-black">{previewData?.taxLabel}:</span>
                          {previewData?.taxAmount > 0 && (
                            <div
                              className="relative"
                              onMouseEnter={() => setShowTaxTooltip(true)}
                              onMouseLeave={() => setShowTaxTooltip(false)}
                            >
                              <IoInformationCircle className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700" />

                              {/* Tax Breakdown Tooltip */}
                              {showTaxTooltip && (
                                <div className="absolute left-0 bottom-full mb-2 w-48 bg-white text-black text-xs rounded-lg p-3 shadow-lg z-10">
                                  <div className="font-semibold mb-2 text-sm">Tax Breakdown:</div>
                                  <div className="space-y-1">
                                    <div className="flex justify-between">
                                      <span>CGST :</span>
                                      <span>₹{formatCurrency(previewData?.cgstAmount || 0)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>SGST :</span>
                                      <span>₹{formatCurrency(previewData?.sgstAmount || 0)}</span>
                                    </div>
                                  </div>
                                  <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-800"></div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <span className="font-medium text-black">
                          {previewData?.taxAmount > 0 ? (
                            `₹${formatCurrency(previewData.taxAmount || 0)}`
                          ) : (
                            <span className="text-gray-400">No charges</span>
                          )}
                        </span>
                      </div>

                      <div className="pt-2 border-t border-gray-200 flex justify-between">
                        <span className="font-semibold text-gray-800">Total:</span>
                        <span className="font-bold text-black">
                          ₹{formatCurrency(previewData?.finalAmount || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Special Instructions */}
                {formData.specialinstructions && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <h4 className="font-medium text-gray-700 mb-2">Special Instructions</h4>
                    <p className="text-gray-700 italic">"{formData.specialinstructions}"</p>
                  </div>
                )}
              </div>

              {/* Footer with action buttons */}
              <div className="px-6 pb-6 pt-2 bg-gray-50 border-t border-gray-100">
                <button
                  onClick={closePreview}
                  className="w-full py-3 px-4 bg-black rounded-lg font-semibold text-white hover:bg-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
                >
                  Back to Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </>
  );
};

export default AmenityBooking;

// import React, { useState, useEffect } from "react";
// import { IoClose, IoInformationCircle } from "react-icons/io5";
// import { toast, ToastContainer } from "react-toastify";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const AmenityBooking = ({
//   formData,
//   setFormData,
//   selectedService,
//   onClose,
//   confirmServiceBooking,
// }) => {
//   const [subcategories, setSubcategories] = useState([]);
//   const [items, setItems] = useState([]);
//   const [allItems, setAllItems] = useState([]);
//   const [previewData, setPreviewData] = useState(null);
//   const [showPreview, setShowPreview] = useState(false);
//   const [isBooking, setIsBooking] = useState(false);
//   const [showTaxTooltip, setShowTaxTooltip] = useState(false);

//   // Tax calculation function - UPDATED
//   const calculateTaxesAndCharges = (
//     item,
//     baseAmount,
//     urgentAmount,
//     urgencyLevel
//   ) => {
//     if (!item || !item.value || !item.value_type) {
//       return {
//         taxAmount: 0,
//         taxLabel: "Taxes & Charges",
//         subtotal: urgencyLevel === "Urgent" ? urgentAmount : baseAmount,
//         finalAmount: urgencyLevel === "Urgent" ? urgentAmount : baseAmount,
//         cgstAmount: 0,
//         sgstAmount: 0,
//       };
//     }

//     const { value, value_type } = item;

//     // Calculate subtotal (include urgent surcharge if applicable)
//     const subtotal = urgencyLevel === "Urgent" ? urgentAmount : baseAmount;

//     let taxAmount = 0;
//     let taxLabel = "Taxes & Charges";

//     if (value_type === "P") {
//       // Percentage
//       taxAmount = (subtotal * value) / 100;
//       taxLabel = `Taxes & Charges (${value}%)`;
//     } else if (value_type === "F") {
//       // Fixed amount
//       taxAmount = value;
//       taxLabel = "Taxes & Charges";
//     }

//     // Calculate CGST and SGST (50% each of total tax)
//     const cgstAmount = taxAmount / 2;
//     const sgstAmount = taxAmount / 2;

//     return {
//       taxAmount,
//       taxLabel,
//       subtotal: subtotal, // This now includes urgent surcharge if applicable
//       finalAmount: subtotal + taxAmount,
//       cgstAmount,
//       sgstAmount,
//     };
//   };

//   // Fetch all items once when component mounts
//   useEffect(() => {
//     const fetchAllItems = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityitems/`);
//         const data = await response.json();
//         setAllItems(data);
//       } catch (error) {
//         console.error("Error fetching all items:", error);
//       }
//     };
//     fetchAllItems();
//   }, []);

//   useEffect(() => {
//     const fetchSubcategories = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityservices/`);
//         const data = await response.json();

//         // Filter services under the selected category
//         const filteredSubcategories = data.filter(
//           (sub) => sub.categoryid == selectedService?.id
//         );

//         setSubcategories(filteredSubcategories);

//         // Reset serviceid when category changes
//         setFormData((prev) => ({ ...prev, serviceid: "" }));
//       } catch (error) {
//         console.error("Error fetching subcategories:", error);
//       }
//     };

//     if (selectedService) {
//       fetchSubcategories();
//     }
//   }, [selectedService, setFormData]);

//   useEffect(() => {
//     if (formData.serviceid) {
//       // Filter items based on service ID
//       const filteredItems = allItems.filter(
//         (item) => item.serviceid == formData.serviceid
//       );
//       setItems(filteredItems);
//     } else if (selectedService?.id) {
//       // If no service selected but category is selected, show items for that category
//       const filteredItems = allItems.filter(
//         (item) => item.categoryid == selectedService.id
//       );
//       setItems(filteredItems);
//     }
//   }, [formData.serviceid, allItems, selectedService]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handlePreview = async () => {
//     // Only require quantity if an item is selected
//     if (formData.itemid && !formData.quantity) {
//       alert("Please enter quantity for the selected item");
//       return;
//     }

//     try {
//       let previewData;
//       let selectedItem = null;

//       if (formData.itemid) {
//         // Get the selected item to ensure we're previewing the correct one
//         selectedItem = items.find((item) => item.id == formData.itemid);
//         if (!selectedItem) {
//           throw new Error("Selected item not found");
//         }

//         const response = await fetch(
//           `${BQ_BASE_URL}/bq/api/amenityrequests/preview/${formData.itemid}/${
//             formData.quantity || 1
//           }`
//         );
//         previewData = await response.json();

//         // Verify the preview data matches the selected item
//         if (previewData.item_name !== selectedItem.name) {
//           console.warn(
//             "Preview data doesn't match selected item. Using local item data."
//           );
//           previewData.base_amount =
//             selectedItem.baseprice * (formData.quantity || 1);
//           previewData.urgent_amount =
//             selectedItem.urgentprice * (formData.quantity || 1);
//           previewData.item_name = selectedItem.name;
//         }

//         // Calculate taxes and charges - UPDATED: Pass urgencyLevel
//         const taxCalculation = calculateTaxesAndCharges(
//           selectedItem,
//           previewData.base_amount,
//           previewData.urgent_amount,
//           formData.urgencylevel || "Normal"
//         );

//         previewData = {
//           ...previewData,
//           ...taxCalculation,
//           selectedItem: selectedItem, // Include the selected item for reference
//         };
//       } else {
//         // For default items (no specific item selected)
//         previewData = {
//           item_name: "Default Service",
//           base_amount: 0,
//           urgent_amount: 0,
//           quantity: 0,
//           taxAmount: 0,
//           taxLabel: "Taxes & Charges",
//           subtotal: 0,
//           finalAmount: 0,
//           cgstAmount: 0,
//           sgstAmount: 0,
//         };
//       }

//       setPreviewData(previewData);
//       setShowPreview(true);
//     } catch (error) {
//       console.error("Error fetching preview:", error);

//       // Fallback to local calculation if API fails and item is selected
//       if (formData.itemid) {
//         const selectedItem = items.find((item) => item.id == formData.itemid);
//         if (selectedItem) {
//           const baseAmount = selectedItem.baseprice * (formData.quantity || 1);
//           const urgentAmount =
//             selectedItem.urgentprice * (formData.quantity || 1);

//           // Calculate taxes and charges - UPDATED: Pass urgencyLevel
//           const taxCalculation = calculateTaxesAndCharges(
//             selectedItem,
//             baseAmount,
//             urgentAmount,
//             formData.urgencylevel || "Normal"
//           );

//           setPreviewData({
//             item_name: selectedItem.name,
//             base_amount: baseAmount,
//             urgent_amount: urgentAmount,
//             quantity: formData.quantity || 1,
//             ...taxCalculation,
//             selectedItem: selectedItem, // Include the selected item for reference
//           });
//           setShowPreview(true);
//         } else {
//           alert("Failed to fetch preview data");
//         }
//       } else {
//         // For default items when API fails
//         setPreviewData({
//           item_name: "Default Service",
//           base_amount: 0,
//           urgent_amount: 0,
//           quantity: 0,
//           taxAmount: 0,
//           taxLabel: "Taxes & Charges",
//           subtotal: 0,
//           finalAmount: 0,
//           cgstAmount: 0,
//           sgstAmount: 0,
//         });
//         setShowPreview(true);
//       }
//     }
//   };

//   const onBook = async (formData) => {
//     const apiUrl = `${BQ_BASE_URL}/bq/api/amenityrequests/`;

//     // Retrieve booking details from localStorage
//     const bookingDetails =
//       JSON.parse(localStorage.getItem("bookingDetails")) || {};

//     // Extract booking ID from nested structure
//     const bookingId =
//       bookingDetails?.primaryBooking?.booking_id ||
//       bookingDetails?.bookings?.[0]?.booking_id ||
//       bookingDetails?.bookingid;

//     if (!bookingId || typeof bookingId !== "string") {
//       toast.error("Invalid booking ID. Please try again.");
//       return;
//     }

//     // Calculate the amount only if an item is selected
//     let amount = 0;
//     let selectedItem = null;

//     if (formData.itemid) {
//       selectedItem = items.find((item) => item.id == formData.itemid);
//       if (!selectedItem) {
//         toast.error("Selected item not found. Please try again.");
//         return;
//       }

//       const baseAmount = selectedItem.baseprice * (formData.quantity || 1);
//       const urgentAmount = selectedItem.urgentprice * (formData.quantity || 1);

//       // Calculate subtotal (include urgent surcharge if applicable) - UPDATED
//       const subtotal =
//         formData.urgencylevel === "Urgent" ? urgentAmount : baseAmount;

//       // Calculate tax on the subtotal - UPDATED
//       let taxAmount = 0;
//       if (selectedItem.value && selectedItem.value_type) {
//         if (selectedItem.value_type === "P") {
//           taxAmount = (subtotal * selectedItem.value) / 100;
//         } else if (selectedItem.value_type === "F") {
//           taxAmount = selectedItem.value;
//         }
//       }

//       amount = subtotal + taxAmount;
//     }

//     // Prepare the request payload
//     const requestBody = {
//       bookingid: bookingId,
//       categoryid: selectedService?.id,
//       serviceid: formData.serviceid,
//       itemid: formData.itemid || null, // Allow null for default items
//       quantity: formData.quantity || null, // Allow null for default items
//       amount: amount,
//       urgencylevel: formData.urgencylevel || "Normal",
//       scheduledtime: formData.scheduledtime,
//       specialinstructions: formData.specialinstructions || "",
//       status: "Pending",
//     };

//     try {
//       const response = await fetch(apiUrl, {
//         method: "POST",
//         headers: {
//           accept: "application/json",
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify(requestBody),
//       });

//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }

//       const data = await response.json();
//       console.log("API Response:", data);
//       toast.success("Amenity service booked successfully!");

//       await confirmServiceBooking();
//       onClose();

//       return data;
//     } catch (error) {
//       console.error("Error making booking:", error);
//       toast.error(`Booking failed: ${error.message}`);
//       throw error;
//     }
//   };

//   const closePreview = () => {
//     setShowPreview(false);
//     setPreviewData(null);
//     setShowTaxTooltip(false);
//   };

//   const handleDateTimeChange = (e) => {
//     const { name, value } = e.target;

//     setFormData((prev) => {
//       const currentDate =
//         prev.scheduledtime?.split("T")[0] ||
//         new Date().toISOString().split("T")[0];
//       const currentTime =
//         prev.scheduledtime?.split("T")[1]?.substring(0, 5) || "00:00";

//       let newDate = currentDate;
//       let newTime = currentTime;

//       if (name === "date") {
//         newDate = value;
//       } else if (name === "time") {
//         newTime = value;
//       }

//       return {
//         ...prev,
//         scheduledtime: `${newDate}T${newTime}:00.000Z`,
//       };
//     });
//   };

//   const formatCurrency = (amount) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "decimal",
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     }).format(amount);
//   };

//   return (
//     <>
//       {/* Main Booking Modal */}
//       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
//         <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
//           <div className="p-6 relative">
//             <div className="flex justify-between  items-start mb-6">
//               <div>
//                 <h2 className="text-2xl font-bold font-poppins text-gray-800">
//                   Book {selectedService?.name}
//                 </h2>
//                 <p className="text-gray-500 font-poppins text-sm mt-1">
//                   Fill in the details to schedule your service
//                 </p>
//               </div>
//               <button
//                 className="text-red-500 hover:text-red-700 transition-colors duration-200"
//                 onClick={onClose}
//               >
//                 <IoClose size={32} />
//               </button>
//             </div>

//             <input
//               type="hidden"
//               name="categoryid"
//               value={selectedService?.id}
//             />
//             <input type="hidden" name="status" value="Pending" />

//             <div className="grid grid-cols-2 gap-6">
//               {/* Service and Item in one row */}
//               <div className="col-span-1">
//                 <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                   Select a Service
//                 </label>
//                 <select
//                   name="serviceid"
//                   value={formData.serviceid}
//                   onChange={handleInputChange}
//                   className="w-full px-4 py-2.5 border font-poppins border-gray-200 rounded-lg duration-200"
//                 >
//                   <option value="">Select a service </option>
//                   {subcategories.map((sub) => (
//                     <option key={sub.id} value={sub.id}>
//                       {sub.name}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               {/* Items Dropdown - now optional */}
//               <div className="col-span-1">
//                 <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                   Select an Item
//                 </label>
//                 <select
//                   name="itemid"
//                   value={formData.itemid || ""}
//                   onChange={handleInputChange}
//                   className="w-full px-4 py-2.5 font-poppins border border-gray-200 rounded-lg duration-200"
//                 >
//                   <option value="">Select an item </option>
//                   {items.map((item) => (
//                     <option key={item.id} value={item.id}>
//                       {item.name}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               {/* Urgency Level and Quantity in one row */}
//               <div className="col-span-1">
//                 <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                   Urgency Level
//                 </label>
//                 <select
//                   name="urgencylevel"
//                   value={formData.urgencylevel || "Normal"}
//                   onChange={handleInputChange}
//                   className="w-full px-4 font-poppins py-2.5 border border-gray-200 rounded-lg duration-200"
//                 >
//                   <option value="Normal">Normal</option>
//                   <option value="Urgent">Urgent</option>
//                 </select>
//               </div>

//               <div className="col-span-1">
//                 <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                   Quantity{" "}
//                   {formData.itemid && <span className="text-red-500">*</span>}
//                 </label>
//                 <input
//                   type="number"
//                   name="quantity"
//                   placeholder="Enter no of quantity"
//                   min="1"
//                   value={formData.quantity || ""}
//                   onChange={handleInputChange}
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   required={!!formData.itemid}
//                   disabled={!formData.itemid}
//                 />
//               </div>

//               <div className="col-span-1">
//                 <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                   Schedule Date <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="date"
//                   name="date"
//                   value={formData.scheduledtime?.split("T")[0] || ""}
//                   onChange={handleDateTimeChange}
//                   min={new Date().toISOString().split("T")[0]}
//                   className="w-full font-poppins px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   required
//                 />
//               </div>

//               {/* Schedule Time */}
//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-md font-poppins font-medium mb-1">
//                   Schedule Time <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="time"
//                   name="time"
//                   value={
//                     formData.scheduledtime?.split("T")[1]?.substring(0, 5) || ""
//                   }
//                   onChange={handleDateTimeChange}
//                   className="w-full px-4 py-2.5 border font-poppins border-gray-200 rounded-lg duration-200"
//                   required
//                 />
//               </div>

//               {/* Special Instructions - full width */}
//               <div className="col-span-2">
//                 <label className="block text-gray-700 font-poppins text-md font-medium mb-1">
//                   Special Instructions
//                 </label>
//                 <textarea
//                   name="specialinstructions"
//                   value={formData.specialinstructions}
//                   onChange={handleInputChange}
//                   rows="3"
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   placeholder="Any special requests or instructions"
//                 ></textarea>
//               </div>
//             </div>

//             {/* Buttons */}
//             <div className="mt-8 flex justify-end space-x-4">
//               <button
//                 onClick={handlePreview}
//                 className="px-10 py-2.5 font-poppins bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
//               >
//                 Preview
//               </button>
//               <button
//                 onClick={async () => {
//                   setIsBooking(true);
//                   try {
//                     await onBook(formData);
//                   } catch (error) {
//                     // Error is already handled by the toast in onBook
//                   } finally {
//                     setIsBooking(false);
//                   }
//                 }}
//                 disabled={isBooking}
//                 className={`px-6 py-2.5 font-poppins  text-white font-medium rounded-lg transition-colors duration-200 bg-black hover:text-gray-300 ${
//                   isBooking ? "opacity-70 cursor-not-allowed" : ""
//                 }`}
//               >
//                 {isBooking ? (
//                   <span className="flex font-poppins items-center justify-center">
//                     <svg
//                       className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                       xmlns="http://www.w3.org/2000/svg"
//                       fill="none"
//                       viewBox="0 0 24 24"
//                     >
//                       <circle
//                         className="opacity-25"
//                         cx="12"
//                         cy="12"
//                         r="10"
//                         stroke="currentColor"
//                         strokeWidth="4"
//                       ></circle>
//                       <path
//                         className="opacity-75"
//                         fill="currentColor"
//                         d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                       ></path>
//                     </svg>
//                     Processing...
//                   </span>
//                 ) : (
//                   "Book Now"
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Preview Modal */}
//       {showPreview && (
//         <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
//           <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-scaleIn">
//             <div className="relative">
//               {/* Header with gradient background */}
//               <div className="bg-gradient-to-r from-black to-gray-700 p-6">
//                 <div className="flex justify-between items-start">
//                   <div>
//                     <h2 className="text-2xl font-bold text-white">
//                       Order Summary
//                     </h2>
//                     <p className="text-blue-100 text-sm mt-1">
//                       Review your order before confirmation
//                     </p>
//                   </div>
//                   <button
//                     className="text-white hover:text-blue-200 transition-colors duration-200 p-1 rounded-full hover:bg-white/10"
//                     onClick={closePreview}
//                   >
//                     <IoClose size={24} />
//                   </button>
//                 </div>
//               </div>

//               {/* Content */}
//               <div className="p-6 space-y-5">
//                 {/* Order Items Card */}
//                 <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
//                   <div className="flex items-center space-x-3 mb-3">
//                     <div className="bg-blue-100 p-2 rounded-lg">
//                       <svg
//                         className="w-6 h-6 text-blue-600"
//                         fill="none"
//                         stroke="currentColor"
//                         viewBox="0 0 24 24"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
//                         />
//                       </svg>
//                     </div>
//                     <div>
//                       <h3 className="font-semibold text-gray-800">
//                         {formData.itemid
//                           ? previewData?.item_name
//                           : "Default Service"}
//                       </h3>
//                       {formData.itemid && (
//                         <p className="text-sm text-gray-500">
//                           Qty: {previewData?.quantity}
//                         </p>
//                       )}
//                     </div>
//                   </div>

//                   <div className="space-y-3 pt-3 border-t border-gray-200">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Urgency:</span>
//                       <span
//                         className={`font-medium ${
//                           formData.urgencylevel === "Urgent"
//                             ? "text-red-500"
//                             : "text-gray-700"
//                         }`}
//                       >
//                         {formData.urgencylevel || "Normal"}
//                       </span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Scheduled for:</span>
//                       <span className="font-medium text-gray-700">
//                         {new Date(formData.scheduledtime).toLocaleString()}
//                       </span>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Pricing Breakdown - only show if item is selected */}
//                 {formData.itemid && (
//                   <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
//                     <h4 className="font-semibold text-black mb-4 text-lg">
//                       Price Details
//                     </h4>
//                     <div className="space-y-2">
//                       {/* Subtotal now includes urgent surcharge if applicable */}
//                       <div className="flex justify-between">
//                         <span className="text-black">Subtotal:</span>
//                         <span className="font-medium">
//                           ₹{formatCurrency(previewData?.subtotal || 0)}
//                           {formData.urgencylevel === "Urgent" && (
//                             <span className="text-xs text-gray-500 ml-1"></span>
//                           )}
//                         </span>
//                       </div>

//                       {/* Taxes and Charges with Info Icon */}
//                       <div className="flex justify-between items-center">
//                         <div className="flex items-center space-x-1">
//                           <span className="text-black">
//                             {previewData?.taxLabel}:
//                           </span>
//                           {previewData?.taxAmount > 0 && (
//                             <div
//                               className="relative"
//                               onMouseEnter={() => setShowTaxTooltip(true)}
//                               onMouseLeave={() => setShowTaxTooltip(false)}
//                             >
//                               <IoInformationCircle className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700" />

//                               {/* Tax Breakdown Tooltip */}
//                               {showTaxTooltip && (
//                                 <div className="absolute left-0 bottom-full mb-2 w-48 bg-white text-black text-xs rounded-lg p-3 shadow-lg z-10">
//                                   <div className="font-semibold mb-2 text-sm">
//                                     Tax Breakdown:
//                                   </div>
//                                   <div className="space-y-1">
//                                     <div className="flex justify-between">
//                                       <span>CGST (6%):</span>
//                                       <span>
//                                         ₹
//                                         {formatCurrency(
//                                           previewData?.cgstAmount || 0
//                                         )}
//                                       </span>
//                                     </div>
//                                     <div className="flex justify-between">
//                                       <span>SGST (6%):</span>
//                                       <span>
//                                         ₹
//                                         {formatCurrency(
//                                           previewData?.sgstAmount || 0
//                                         )}
//                                       </span>
//                                     </div>
//                                   </div>
//                                   {/* Tooltip arrow */}
//                                   <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-800"></div>
//                                 </div>
//                               )}
//                             </div>
//                           )}
//                         </div>
//                         <span className="font-medium text-black">
//                           {previewData?.taxAmount > 0 ? (
//                             `₹${formatCurrency(previewData.taxAmount || 0)}`
//                           ) : (
//                             <span className="text-gray-400">No charges</span>
//                           )}
//                         </span>
//                       </div>

//                       {/* Removed separate urgent surcharge line since it's now included in subtotal */}

//                       <div className="pt-2 border-t border-gray-200 flex justify-between">
//                         <span className="font-semibold text-gray-800">
//                           Total:
//                         </span>
//                         <span className="font-bold text-black">
//                           ₹{formatCurrency(previewData?.finalAmount || 0)}
//                         </span>
//                       </div>
//                     </div>
//                   </div>
//                 )}

//                 {/* Special Instructions */}
//                 {formData.specialinstructions && (
//                   <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
//                     <h4 className="font-medium text-gray-700 mb-2">
//                       Special Instructions
//                     </h4>
//                     <p className="text-gray-700 italic">
//                       "{formData.specialinstructions}"
//                     </p>
//                   </div>
//                 )}
//               </div>

//               {/* Footer with action buttons */}
//               <div className="px-6 pb-6 pt-2 bg-gray-50 border-t border-gray-100">
//                 <button
//                   onClick={closePreview}
//                   className="w-full py-3 px-4 bg-black rounded-lg font-semibold text-white hover:bg-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
//                 >
//                   Back to Edit
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//       <ToastContainer />
//     </>
//   );
// };

// export default AmenityBooking;
